import { lazy, Suspense, type ReactElement } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import Layout from './components/Layout'
import { LOCALES, ROUTES, type PageKey } from './lib/i18n'
import ContentPage from './pages/ContentPage'
import Galerie from './pages/Galerie'
import Home from './pages/Home'
import Klang from './pages/Klang'
import Kontakt from './pages/Kontakt'
import NotFound from './pages/NotFound'
import ProductDetail from './pages/ProductDetail'
import Reparaturen from './pages/Reparaturen'
import Shop from './pages/Shop'
import UeberMich from './pages/UeberMich'

// The admin bundle is loaded on demand - visitors never download it.
const AdminApp = lazy(() => import('./admin/AdminApp'))

/** Old WordPress URLs that must keep working. The client's links, printed
 *  material and search rankings all point at these. Note: no trailing-slash
 *  entries - React Router matches "/foo/" with a "/foo" route already, and a
 *  "/foo/" pattern would shadow the real route and redirect it to itself. */
const LEGACY_REDIRECTS: [string, string][] = [
  ['/en/home', '/en'],
  // The original privacy-policy slug carried a typo ("erkaerung").
  ['/datenschutzerkaerung', '/datenschutz'],
  ['/warenkorb', '/kontakt'],
  ['/kasse', '/kontakt'],
  ['/mein-konto', '/kontakt'],
]

/** One element per page key; routes for every locale are generated from
 *  ROUTES so a new language never means another block of route literals. */
const PAGE_ELEMENTS: Record<PageKey, ReactElement> = {
  home: <Home />,
  uebermich: <UeberMich />,
  klang: <Klang />,
  galerie: <Galerie />,
  shop: <Shop />,
  service: <Reparaturen />,
  kontakt: <Kontakt />,
  impressum: <ContentPage pageKey="impressum" />,
  datenschutz: <ContentPage pageKey="datenschutz" />,
}

export default function App() {
  return (
    <Routes>
      <Route element={<Layout />}>
        {LOCALES.flatMap((locale) => [
          ...(Object.keys(PAGE_ELEMENTS) as PageKey[]).map((key) => (
            <Route
              key={`${locale}-${key}`}
              path={ROUTES[locale][key]}
              element={PAGE_ELEMENTS[key]}
            />
          )),
          <Route
            key={`${locale}-product`}
            path={`${ROUTES[locale].shop}/:slug`}
            element={<ProductDetail />}
          />,
        ])}

        {LEGACY_REDIRECTS.map(([from, to]) => (
          <Route key={from} path={from} element={<Navigate to={to} replace />} />
        ))}

        <Route path="*" element={<NotFound />} />
      </Route>

      <Route
        path="/admin/*"
        element={
          <Suspense fallback={<div className="p-8 font-sans text-sm text-slate">…</div>}>
            <AdminApp />
          </Suspense>
        }
      />
    </Routes>
  )
}
